/**
 * UTM Parameter Tracker
 * Captures and persists campaign attribution data for marketing analytics
 */
import { normalizeAffiliateCode } from "@/lib/affiliate-onboarding";
import { createLogger } from "@/lib/logger";

const logger = createLogger("utm-tracker");

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface AttributionData extends UTMParams {
  referrer?: string;
  landing_page?: string;
  first_visit?: string;
  last_visit?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  dclid?: string;
  gad_source?: string;
  gad_campaignid?: string;
  fbclid?: string;
  /** Opaque OpenAI Ads click reference. Preserve unchanged and never emit to analytics logs. */
  oppref?: string;
  // RevenueCat specific attribution fields
  network?: string;
  ad_group?: string;
  keywords?: string;
  creative?: string;
  latest_paid_source?: string;
  latest_paid_medium?: string;
  latest_paid_campaign?: string;
  latest_paid_term?: string;
  latest_paid_content?: string;
  latest_paid_gclid?: string;
  latest_paid_gbraid?: string;
  latest_paid_wbraid?: string;
  latest_paid_dclid?: string;
  latest_paid_fbclid?: string;
  latest_paid_gad_source?: string;
  latest_paid_gad_campaignid?: string;
  latest_paid_network?: string;
  latest_paid_ad_group?: string;
  latest_paid_keywords?: string;
  latest_paid_creative?: string;
  latest_paid_oppref?: string;
  latest_paid_landing_page?: string;
  latest_paid_referrer?: string;
  latest_paid_visit?: string;
  /** Set when the record was carried into this browser by a sign-in handoff. */
  restored_from?: string;
}

const ATTRIBUTION_KEY = "chicky_attribution";
export const AFFILIATE_ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const NON_ACQUISITION_REFERRER_HOSTS = new Set(["accounts.google.com"]);

// Click IDs identify one ad click; seeing a stored one again is the same touch.
const CLICK_ID_LATEST_PAID_FIELDS = {
  gclid: "latest_paid_gclid",
  gbraid: "latest_paid_gbraid",
  wbraid: "latest_paid_wbraid",
  dclid: "latest_paid_dclid",
  fbclid: "latest_paid_fbclid",
  oppref: "latest_paid_oppref",
} as const satisfies Record<string, keyof AttributionData | undefined>;

const setDefined = (target: Record<string, string>, key: string, value?: string) => {
  if (value) target[key] = value;
};

function isSameSiteHost(host: string): boolean {
  if (typeof window === "undefined") return false;

  const siteHost = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const referrerHost = host.replace(/^www\./, "");
  // Use our known apex on landing/links/etc. too, including sibling referrers.
  // Keep other hosts scoped to themselves rather than guessing public suffixes.
  const siteDomain =
    siteHost === "chickytutor.com" || siteHost.endsWith(".chickytutor.com")
      ? "chickytutor.com"
      : siteHost;
  return !!siteDomain && (referrerHost === siteDomain || referrerHost.endsWith(`.${siteDomain}`));
}

function isNonAcquisitionReferrer(referrer?: string): boolean {
  if (!referrer) return false;

  try {
    const host = new URL(referrer).hostname.toLowerCase();
    // Internal navigation is never acquisition evidence.
    return NON_ACQUISITION_REFERRER_HOSTS.has(host) || isSameSiteHost(host);
  } catch {
    return false;
  }
}

function repeatsStoredClick(
  existing: AttributionData | null,
  params: Partial<Record<keyof typeof CLICK_ID_LATEST_PAID_FIELDS, string>>
): boolean {
  if (!existing) return false;

  return (Object.keys(CLICK_ID_LATEST_PAID_FIELDS) as (keyof typeof CLICK_ID_LATEST_PAID_FIELDS)[])
    .filter((field) => params[field])
    .some((field) => {
      const latestPaidField = CLICK_ID_LATEST_PAID_FIELDS[field];
      return (
        params[field] === existing[field] ||
        (!!latestPaidField && params[field] === existing[latestPaidField])
      );
    });
}

export function hasAttributionSignal(attribution: AttributionData | null | undefined): boolean {
  return !!(
    attribution?.utm_source ||
    attribution?.utm_medium ||
    attribution?.utm_campaign ||
    attribution?.utm_term ||
    attribution?.utm_content ||
    attribution?.referrer ||
    attribution?.gclid ||
    attribution?.gbraid ||
    attribution?.wbraid ||
    attribution?.dclid ||
    attribution?.gad_campaignid ||
    attribution?.fbclid ||
    attribution?.oppref ||
    attribution?.network ||
    attribution?.ad_group ||
    attribution?.keywords ||
    attribution?.creative
  );
}

function hasCampaignAttributionSignal(attribution: AttributionData | null | undefined): boolean {
  return !!(
    attribution?.utm_source ||
    attribution?.utm_medium ||
    attribution?.utm_campaign ||
    attribution?.utm_term ||
    attribution?.utm_content ||
    attribution?.gclid ||
    attribution?.gbraid ||
    attribution?.wbraid ||
    attribution?.dclid ||
    attribution?.gad_campaignid ||
    attribution?.fbclid ||
    attribution?.oppref ||
    attribution?.network ||
    attribution?.ad_group ||
    attribution?.keywords ||
    attribution?.creative
  );
}

export function isAffiliateReferralTouch(attribution: AttributionData | null | undefined): boolean {
  const campaign = attribution?.utm_campaign?.trim().toLowerCase() || "";
  return (
    attribution?.utm_source?.toLowerCase() === "affiliate" &&
    attribution?.utm_medium?.toLowerCase() === "referral" &&
    normalizeAffiliateCode(campaign) === campaign
  );
}

function isPaidAttributionTouch(
  attribution:
    | (UTMParams & {
        gclid?: string;
        gbraid?: string;
        wbraid?: string;
        dclid?: string;
        gad_campaignid?: string;
        fbclid?: string;
        oppref?: string;
        ad_group?: string;
        keywords?: string;
        creative?: string;
      })
    | null
    | undefined
): boolean {
  const paidMediums = new Set(["cpc", "ppc", "paid", "paid_social", "paid_video", "display"]);
  const paidSources = new Set([
    "google_ads",
    "googleadwords",
    "adwords",
    "facebook_ads",
    "meta_ads",
    "tiktok_ads",
    "linkedin_ads",
    "x_ads",
    "noise",
  ]);
  const source = attribution?.utm_source?.toLowerCase();
  const medium = attribution?.utm_medium?.toLowerCase();

  return !!(
    attribution?.gclid ||
    attribution?.gbraid ||
    attribution?.wbraid ||
    attribution?.dclid ||
    attribution?.gad_campaignid ||
    attribution?.fbclid ||
    attribution?.oppref ||
    attribution?.ad_group ||
    attribution?.keywords ||
    attribution?.creative ||
    (source && paidSources.has(source)) ||
    (medium && paidMediums.has(medium))
  );
}

function inferPaidSource(
  attribution: {
    utm_source?: string;
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    dclid?: string;
    gad_campaignid?: string;
    fbclid?: string;
    oppref?: string;
  },
  source?: string
): string | undefined {
  if (source) return source;
  if (
    attribution.gclid ||
    attribution.gbraid ||
    attribution.wbraid ||
    attribution.dclid ||
    attribution.gad_campaignid
  ) {
    return "google_ads";
  }
  if (attribution.fbclid) return "facebook_ads";
  if (attribution.oppref) return "chatgpt_ads";
  return undefined;
}

/**
 * Stable first-touch properties for PostHog super properties and event payloads.
 * Keep this separate from PostHog's native UTM fields so queries can distinguish
 * repo-owned first-touch attribution from PostHog's latest/session properties.
 */
export function buildInitialAttributionProperties(
  attribution: AttributionData | null | undefined
): Record<string, string> {
  if (!attribution) return {};

  const properties: Record<string, string> = {};
  setDefined(properties, "initial_utm_source", attribution.utm_source);
  setDefined(properties, "initial_utm_medium", attribution.utm_medium);
  setDefined(properties, "initial_utm_campaign", attribution.utm_campaign);
  setDefined(properties, "initial_utm_term", attribution.utm_term);
  setDefined(properties, "initial_utm_content", attribution.utm_content);
  setDefined(properties, "initial_referrer", attribution.referrer);
  setDefined(properties, "initial_landing_page", attribution.landing_page);
  setDefined(properties, "first_visit", attribution.first_visit);
  setDefined(properties, "initial_gclid", attribution.gclid);
  setDefined(properties, "initial_gbraid", attribution.gbraid);
  setDefined(properties, "initial_wbraid", attribution.wbraid);
  setDefined(properties, "initial_dclid", attribution.dclid);
  setDefined(properties, "initial_gad_source", attribution.gad_source);
  setDefined(properties, "initial_gad_campaignid", attribution.gad_campaignid);
  setDefined(properties, "initial_fbclid", attribution.fbclid);
  setDefined(properties, "initial_ad_network", attribution.network);
  setDefined(properties, "initial_ad_group", attribution.ad_group);
  setDefined(properties, "initial_keywords", attribution.keywords);
  setDefined(properties, "initial_creative", attribution.creative);
  return properties;
}

function buildLatestPaidAttributionProperties(
  attribution: AttributionData | null | undefined
): Record<string, string> {
  if (!attribution) return {};

  const properties: Record<string, string> = {};
  setDefined(properties, "latest_paid_source", attribution.latest_paid_source);
  setDefined(properties, "latest_paid_medium", attribution.latest_paid_medium);
  setDefined(properties, "latest_paid_campaign", attribution.latest_paid_campaign);
  setDefined(properties, "latest_paid_term", attribution.latest_paid_term);
  setDefined(properties, "latest_paid_content", attribution.latest_paid_content);
  setDefined(properties, "latest_paid_gclid", attribution.latest_paid_gclid);
  setDefined(properties, "latest_paid_gbraid", attribution.latest_paid_gbraid);
  setDefined(properties, "latest_paid_wbraid", attribution.latest_paid_wbraid);
  setDefined(properties, "latest_paid_dclid", attribution.latest_paid_dclid);
  setDefined(properties, "latest_paid_gad_source", attribution.latest_paid_gad_source);
  setDefined(properties, "latest_paid_gad_campaignid", attribution.latest_paid_gad_campaignid);
  setDefined(properties, "latest_paid_network", attribution.latest_paid_network);
  setDefined(properties, "latest_paid_ad_group", attribution.latest_paid_ad_group);
  setDefined(properties, "latest_paid_keywords", attribution.latest_paid_keywords);
  setDefined(properties, "latest_paid_creative", attribution.latest_paid_creative);
  setDefined(properties, "latest_paid_landing_page", attribution.latest_paid_landing_page);
  setDefined(properties, "latest_paid_referrer", attribution.latest_paid_referrer);
  setDefined(properties, "latest_paid_visit", attribution.latest_paid_visit);
  return properties;
}

export function buildEventAttributionProperties(
  attribution: AttributionData | null | undefined
): Record<string, string> {
  if (!attribution) return {};

  const properties: Record<string, string> = {
    ...buildInitialAttributionProperties(attribution),
    ...buildLatestPaidAttributionProperties(attribution),
    attribution_model: "first_touch",
  };

  setDefined(properties, "attribution_source", attribution.utm_source);
  setDefined(properties, "attribution_medium", attribution.utm_medium);
  setDefined(properties, "attribution_campaign", attribution.utm_campaign);
  setDefined(properties, "attribution_term", attribution.utm_term);
  setDefined(properties, "attribution_content", attribution.utm_content);
  setDefined(properties, "attribution_click_id_google", attribution.gclid);
  setDefined(properties, "attribution_click_id_gbraid", attribution.gbraid);
  setDefined(properties, "attribution_click_id_wbraid", attribution.wbraid);
  setDefined(properties, "attribution_click_id_dclid", attribution.dclid);
  setDefined(properties, "attribution_click_id_facebook", attribution.fbclid);
  setDefined(properties, "attribution_network", attribution.network);
  setDefined(properties, "attribution_ad_group", attribution.ad_group);
  setDefined(properties, "attribution_keywords", attribution.keywords);
  setDefined(properties, "attribution_creative", attribution.creative);
  setDefined(properties, "attribution_restored_from", attribution.restored_from);
  return properties;
}

export function sanitizeAttributionUrl(url?: string): string | undefined {
  if (!url) return undefined;

  try {
    const parsed = new URL(url);
    parsed.hash = "";

    [
      "access_token",
      "refresh_token",
      "provider_token",
      "provider_refresh_token",
      "expires_at",
      "expires_in",
      "token_type",
      "sb",
      "oppref",
      "ct_attr",
    ].forEach((key) => parsed.searchParams.delete(key));

    return parsed.toString();
  } catch {
    return url.split("#")[0];
  }
}

/**
 * Extract UTM parameters from URL search params
 * Also captures gclid (Google) and fbclid (Facebook) for ad attribution
 * And additional RevenueCat attribution fields
 */
function extractRawQueryParameter(rawSearch: string, parameterName: string): string | undefined {
  const query = rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch;

  for (const part of query.split("&")) {
    const separatorIndex = part.indexOf("=");
    const rawName = separatorIndex === -1 ? part : part.slice(0, separatorIndex);
    if (rawName !== parameterName) continue;

    const rawValue = separatorIndex === -1 ? "" : part.slice(separatorIndex + 1);
    return rawValue || undefined;
  }

  return undefined;
}

export function extractUTMParams(
  searchParams: URLSearchParams,
  rawSearch?: string
): UTMParams & {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  dclid?: string;
  gad_source?: string;
  gad_campaignid?: string;
  fbclid?: string;
  oppref?: string;
  network?: string;
  ad_group?: string;
  keywords?: string;
  creative?: string;
} {
  const utmParams: any = {};

  const params = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

  params.forEach((param) => {
    const value =
      searchParams.get(param) || (param === "utm_source" ? searchParams.get("tm_source") : null);
    if (value) {
      utmParams[param] = value;
    }
  });

  // Capture click IDs for ad platforms
  const gclid = searchParams.get("gclid");
  const gbraid = searchParams.get("gbraid");
  const wbraid = searchParams.get("wbraid");
  const dclid = searchParams.get("dclid");
  const gadSource = searchParams.get("gad_source");
  const gadCampaignId = searchParams.get("gad_campaignid");
  const fbclid = searchParams.get("fbclid");
  const oppref = rawSearch
    ? extractRawQueryParameter(rawSearch, "oppref")
    : searchParams.get("oppref") || undefined;

  if (gclid) utmParams.gclid = gclid;
  if (gbraid) utmParams.gbraid = gbraid;
  if (wbraid) utmParams.wbraid = wbraid;
  if (dclid) utmParams.dclid = dclid;
  if (gadSource) utmParams.gad_source = gadSource;
  if (gadCampaignId) {
    utmParams.gad_campaignid = gadCampaignId;
    if (!utmParams.utm_campaign) {
      utmParams.utm_campaign = gadCampaignId;
    }
  }
  if (fbclid) utmParams.fbclid = fbclid;
  if (oppref) utmParams.oppref = oppref;

  // Capture RevenueCat specific attribution fields
  const network = searchParams.get("network");
  const adGroup = searchParams.get("ad_group");
  const keywords = searchParams.get("keywords");
  const creative = searchParams.get("creative");

  if (network) utmParams.network = network;
  if (adGroup) utmParams.ad_group = adGroup;
  if (keywords) utmParams.keywords = keywords;
  if (creative) utmParams.creative = creative;

  return utmParams;
}

/**
 * Get stored attribution data from localStorage
 */
export function getAttributionData(): AttributionData | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(ATTRIBUTION_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Acquisition history must survive a returning visit. Only the separate
    // affiliate eligibility contract still expires after 30 days.
    if (isAffiliateReferralTouch(data) && data.first_visit) {
      if (Date.now() - Date.parse(data.first_visit) > AFFILIATE_ATTRIBUTION_MAX_AGE_MS) {
        localStorage.removeItem(ATTRIBUTION_KEY);
        return null;
      }
    }

    return data;
  } catch (error) {
    logger.error("Error reading attribution data", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

type TouchParams = UTMParams & {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  dclid?: string;
  gad_source?: string;
  gad_campaignid?: string;
  fbclid?: string;
  oppref?: string;
  network?: string;
  ad_group?: string;
  keywords?: string;
  creative?: string;
};

/**
 * The latest-paid record is replaced as a whole group for each genuine paid
 * touch, so fields from two different clicks never mix.
 */
function buildLatestPaidData(
  touch: TouchParams,
  {
    source,
    medium,
    network,
    landingPage,
    referrer,
    visitedAt,
  }: {
    source?: string;
    medium?: string;
    network?: string;
    landingPage?: string;
    referrer?: string;
    visitedAt: string;
  }
): Partial<AttributionData> {
  return {
    latest_paid_source: inferPaidSource(touch, source),
    latest_paid_medium: medium,
    latest_paid_campaign: touch.utm_campaign || touch.gad_campaignid,
    latest_paid_term: touch.utm_term,
    latest_paid_content: touch.utm_content,
    latest_paid_gclid: touch.gclid,
    latest_paid_gbraid: touch.gbraid,
    latest_paid_wbraid: touch.wbraid,
    latest_paid_dclid: touch.dclid,
    latest_paid_fbclid: touch.fbclid,
    latest_paid_gad_source: touch.gad_source,
    latest_paid_gad_campaignid: touch.gad_campaignid,
    latest_paid_network: network,
    latest_paid_ad_group: touch.ad_group,
    latest_paid_keywords: touch.keywords,
    latest_paid_creative: touch.creative,
    latest_paid_oppref: touch.oppref,
    latest_paid_landing_page: landingPage,
    latest_paid_referrer: referrer,
    latest_paid_visit: visitedAt,
  };
}

/**
 * Restore a record carried across a sign-in redirect into a browser context
 * that has none, such as a sign-in that finished in the system browser. An
 * existing local record always wins, and a restore never counts as a new touch.
 * Returns the restored record, or null when nothing was restored.
 */
export function restoreAttributionData(
  restored: AttributionData,
  provenance: string
): AttributionData | null {
  if (typeof window === "undefined" || getAttributionData()) return null;

  try {
    const now = new Date().toISOString();
    const firstVisit = restored.first_visit || now;
    const record: AttributionData = {
      utm_source: restored.utm_source,
      utm_medium: restored.utm_medium,
      utm_campaign: restored.utm_campaign,
      utm_term: restored.utm_term,
      utm_content: restored.utm_content,
      gclid: restored.gclid,
      gbraid: restored.gbraid,
      wbraid: restored.wbraid,
      dclid: restored.dclid,
      gad_source: restored.gad_source,
      gad_campaignid: restored.gad_campaignid,
      fbclid: restored.fbclid,
      network: restored.network,
      ad_group: restored.ad_group,
      keywords: restored.keywords,
      creative: restored.creative,
      referrer: restored.referrer,
      landing_page: restored.landing_page,
      first_visit: firstVisit,
      last_visit: now,
      restored_from: provenance,
      ...(isPaidAttributionTouch(restored)
        ? buildLatestPaidData(restored, {
            source: restored.utm_source,
            medium: restored.utm_medium,
            network: restored.network,
            landingPage: restored.landing_page,
            referrer: restored.referrer,
            visitedAt: firstVisit,
          })
        : {}),
    };

    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(record));
    return record;
  } catch (error) {
    logger.error("Error restoring attribution data", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Save attribution data to localStorage
 * Uses first-touch attribution model - keeps original source
 * Falls back to inferring source from referrer domain if no UTM params
 */
export function saveAttributionData(
  utmParams: TouchParams,
  referrer?: string,
  landingPage?: string
): AttributionData {
  if (typeof window === "undefined") {
    return { ...utmParams };
  }

  try {
    const existing = getAttributionData();
    const now = new Date().toISOString();
    const documentReferrer = referrer || (typeof document !== "undefined" ? document.referrer : "");
    const acquisitionReferrer = isNonAcquisitionReferrer(documentReferrer) ? "" : documentReferrer;
    const sanitizedReferrer = sanitizeAttributionUrl(acquisitionReferrer) || undefined;
    const sanitizedLandingPage = sanitizeAttributionUrl(landingPage || window.location.href);

    // No existing attribution - determine source from UTM params or referrer
    let finalSource: string | undefined = utmParams.utm_source;
    let finalMedium: string | undefined = utmParams.utm_medium;
    let finalNetwork: string | undefined = utmParams.network;

    // Google auto-tagging need not include UTMs. Its click/campaign evidence
    // must win over referrers and organic tags copied into older internal URLs.
    // Keep explicitly named campaigns and the affiliate routing contract intact.
    const hasGoogleAdsEvidence = !!(
      utmParams.gclid ||
      utmParams.gbraid ||
      utmParams.wbraid ||
      utmParams.dclid ||
      utmParams.gad_campaignid
    );
    if (hasGoogleAdsEvidence && !isAffiliateReferralTouch(utmParams)) {
      if (!finalSource || finalSource.toLowerCase().endsWith("_organic")) {
        finalSource = "google_ads";
      }
      if (
        !finalMedium ||
        ["organic", "referral", "social", "video"].includes(finalMedium.toLowerCase())
      ) {
        finalMedium = "cpc";
      }
      finalNetwork = "Google Ads";
    }

    // An OpenAI Ads click reference is itself sufficient evidence of a paid
    // ChatGPT touch, even when optional campaign query parameters are absent.
    if (utmParams.oppref) {
      finalSource ||= "chatgpt_ads";
      finalMedium ||= "cpc";
      finalNetwork ||= "ChatGPT Ads";
    }

    // Infer network from platform-specific click IDs if not explicitly set
    if (!finalNetwork) {
      if (utmParams.gclid || utmParams.gbraid || utmParams.wbraid || utmParams.dclid) {
        finalNetwork = "Google Ads";
      } else if (utmParams.fbclid) {
        finalNetwork = "Facebook Ads";
      } else if (finalSource) {
        // Map common UTM sources to network names
        const sourceLower = finalSource.toLowerCase();
        if (sourceLower.includes("google") || sourceLower.includes("adwords")) {
          finalNetwork = "Google Ads";
        } else if (sourceLower.includes("facebook") || sourceLower.includes("meta")) {
          finalNetwork = "Facebook Ads";
        } else if (sourceLower.includes("tiktok")) {
          finalNetwork = "TikTok Ads";
        } else if (sourceLower.includes("linkedin")) {
          finalNetwork = "LinkedIn Ads";
        } else if (sourceLower.includes("twitter") || sourceLower.includes("x")) {
          finalNetwork = "X Ads";
        } else if (sourceLower === "noise") {
          finalNetwork = "Noise";
        } else if (sourceLower === "affiliate") {
          finalNetwork = "Affiliate";
        }
      }
    }

    // Only infer from referrer if we have NO existing attribution AND NO UTM params
    if (!finalSource && acquisitionReferrer) {
      try {
        const referrerUrl = new URL(acquisitionReferrer);
        const referrerDomain = referrerUrl.hostname.toLowerCase();

        // Don't infer from localhost or same domain
        if (
          !referrerDomain.includes("localhost") &&
          !referrerDomain.includes(window.location.hostname)
        ) {
          // Map common referrers to UTM source/medium with _organic suffix
          if (
            referrerDomain.includes("chatgpt.com") ||
            referrerDomain.includes("chat.openai.com")
          ) {
            finalSource = "chatgpt_organic";
            finalMedium = "referral";
            finalNetwork = "ChatGPT";
          } else if (referrerDomain.includes("perplexity.ai")) {
            finalSource = "perplexity_organic";
            finalMedium = "referral";
            finalNetwork = "Perplexity";
          } else if (referrerDomain.includes("claude.ai")) {
            finalSource = "claude_organic";
            finalMedium = "referral";
            finalNetwork = "Claude";
          } else if (referrerDomain.includes("gemini.google.com")) {
            finalSource = "gemini_organic";
            finalMedium = "referral";
            finalNetwork = "Gemini";
          } else if (referrerDomain.includes("copilot.microsoft.com")) {
            finalSource = "copilot_organic";
            finalMedium = "referral";
            finalNetwork = "Copilot";
          } else if (referrerDomain.includes("google.com")) {
            if (referrerDomain.includes("googleads") || referrerDomain.includes("doubleclick")) {
              finalSource = "google";
              finalMedium = "cpc";
              finalNetwork = "Google Ads";
            } else {
              finalSource = "google_organic";
              finalMedium = "organic";
            }
          } else if (referrerDomain.includes("facebook.com") || referrerDomain.includes("fb.com")) {
            finalSource = "facebook_organic";
            finalMedium = "social";
            finalNetwork = "Facebook";
          } else if (referrerDomain.includes("reddit.com")) {
            finalSource = "reddit_organic";
            finalMedium = "social";
          } else if (referrerDomain.includes("linkedin.com")) {
            finalSource = "linkedin_organic";
            finalMedium = "social";
            finalNetwork = "LinkedIn";
          } else if (referrerDomain.includes("instagram.com")) {
            finalSource = "instagram_organic";
            finalMedium = "social";
            finalNetwork = "Instagram";
          } else if (referrerDomain.includes("youtube.com")) {
            finalSource = "youtube_organic";
            finalMedium = "video";
            finalNetwork = "YouTube";
          } else if (referrerDomain.includes("bing.com")) {
            finalSource = "bing_organic";
            finalMedium = "organic";
            finalNetwork = "Bing";
          } else if (referrerDomain.includes("twitter.com") || referrerDomain.includes("x.com")) {
            finalSource = "twitter_organic";
            finalMedium = "social";
            finalNetwork = "X";
          } else {
            // Generic external referrer - use domain name with _organic
            const domainParts = referrerDomain.split(".");
            const mainDomain =
              domainParts.length > 2 ? domainParts[domainParts.length - 2] : domainParts[0];
            finalSource = `${mainDomain}_organic`;
            finalMedium = "referral";
            finalNetwork = mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1);
          }
        }
      } catch (error) {
        // Invalid URL, ignore
      }
    }

    const currentAttributionData: AttributionData = {
      utm_source: finalSource,
      utm_medium: finalMedium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
      utm_content: utmParams.utm_content,
      gclid: utmParams.gclid,
      gbraid: utmParams.gbraid,
      wbraid: utmParams.wbraid,
      dclid: utmParams.dclid,
      gad_source: utmParams.gad_source,
      gad_campaignid: utmParams.gad_campaignid,
      fbclid: utmParams.fbclid,
      oppref: utmParams.oppref,
      network: finalNetwork,
      ad_group: utmParams.ad_group,
      keywords: utmParams.keywords,
      creative: utmParams.creative,
      referrer: sanitizedReferrer,
      landing_page: sanitizedLandingPage,
      first_visit: now,
      last_visit: now,
    };
    // A stored click ID seen again (reload, bookmark, or an old bundle's
    // replayed link) is the same touch, not a new paid visit.
    const latestPaidData: Partial<AttributionData> =
      isPaidAttributionTouch(utmParams) && !repeatsStoredClick(existing, utmParams)
        ? buildLatestPaidData(utmParams, {
            source: finalSource,
            medium: finalMedium,
            network: finalNetwork,
            landingPage: sanitizedLandingPage,
            referrer: sanitizedReferrer,
            visitedAt: now,
          })
        : {};

    // Affiliate terms use most-recent eligible affiliate click, while the
    // site's general acquisition analytics remain first-touch. Restart the
    // affiliate window and replace the primary campaign fields, retaining any
    // separately stored latest-paid diagnostics.
    if (existing && isAffiliateReferralTouch(currentAttributionData)) {
      const affiliateAttributionData: AttributionData = {
        ...existing,
        ...currentAttributionData,
        first_visit: now,
        last_visit: now,
      };
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(affiliateAttributionData));
      return affiliateAttributionData;
    }

    if (existing && hasCampaignAttributionSignal(existing)) {
      const attributionData: AttributionData = {
        ...existing,
        ...latestPaidData,
        last_visit: now,
      };
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attributionData));
      return attributionData;
    }

    // A direct visit still owns its original landing page and timestamp while
    // the learner navigates internally or returns from authentication. These
    // fields are intentionally not campaign signals, so preserve them here
    // without preventing a later explicit/referrer-inferred campaign touch
    // from filling otherwise empty acquisition fields.
    if (
      existing &&
      (existing.landing_page || existing.first_visit) &&
      !hasCampaignAttributionSignal(currentAttributionData)
    ) {
      const attributionData: AttributionData = {
        ...existing,
        ...latestPaidData,
        last_visit: now,
      };
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attributionData));
      return attributionData;
    }

    if (existing && hasAttributionSignal(existing)) {
      const attributionData: AttributionData = hasCampaignAttributionSignal(currentAttributionData)
        ? {
            ...currentAttributionData,
            ...latestPaidData,
            referrer: existing.referrer,
            landing_page: existing.landing_page || currentAttributionData.landing_page,
            first_visit: existing.first_visit || now,
            last_visit: now,
          }
        : {
            ...existing,
            ...latestPaidData,
            last_visit: now,
          };
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attributionData));
      return attributionData;
    }

    const attributionData: AttributionData = {
      ...currentAttributionData,
      ...latestPaidData,
    };

    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attributionData));
    return attributionData;
  } catch (error) {
    logger.error("Error saving attribution data", {
      error: error instanceof Error ? error.message : String(error),
    });
    return { ...utmParams };
  }
}

/**
 * Build the minimal purchase metadata needed to recover OpenAI Ads attribution
 * at the RevenueCat webhook boundary. The opaque value is not trimmed,
 * decoded, normalized, or added to general analytics properties.
 */
export function buildOpenAIAdsPurchaseMetadata(
  attribution: AttributionData | null | undefined = getAttributionData()
): Record<string, string> {
  const oppref = attribution?.latest_paid_oppref || attribution?.oppref;
  return oppref ? { openai_ads_oppref: oppref } : {};
}

/**
 * Clear attribution data (useful for testing or user privacy)
 */
export function clearAttributionData(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(ATTRIBUTION_KEY);
}

/**
 * Get campaign summary for display or logging
 */
export function getCampaignSummary(attribution: AttributionData | null): string {
  if (!attribution?.utm_source) {
    return "Direct / Unknown";
  }

  const parts = [attribution.utm_source];
  if (attribution.utm_medium) parts.push(attribution.utm_medium);
  if (attribution.utm_campaign) parts.push(attribution.utm_campaign);

  return parts.join(" / ");
}
